import type {FieldValue,VehicleInput} from './contracts';
import type {VinInformation} from './vehicle-information';
const prefix='NHTSA vPIC manufacturer-reported data';
const properties:Record<string,keyof VehicleInput>={year:'year',make:'make',model:'model',trim:'trim',drivetrain:'drivetrain',transmission:'transmission',fuelType:'fuel',vin:'identifier',odometerKm:'odometerKm'};
export function fillMissingVinDetails<T extends VehicleInput>(current:T,info:VinInformation):T {
 if(info.status!=='decoded'||current.identifier?.trim().toUpperCase()!==info.vin)return current;
 const next={...current,fields:{...current.fields}};
 const source=`${prefix} · checked ${info.checkedAt} · ${info.sourceUrl}`;
 const add=(id:string,value:string)=>{
  const property=properties[id];
  if(!value.trim()||next.fields[id]?.value?.trim()||(property&&current[property]!=null&&String(current[property]).trim()!==''))return;
  next.fields[id]={value,status:'reported',source} satisfies FieldValue;
  if(property)(next as Record<string,unknown>)[property]=property==='year'?Number(value):value;
 };
 const f=info.facts;
 add('year',f.year===null?'':String(f.year));add('make',f.make);add('model',f.model);add('trim',f.trim);
 add('drivetrain',f.drivetrain);add('transmission',f.transmission);add('fuelType',f.fuelType);
 add('bodyStyle',f.bodyStyle);add('engineDisplacement',f.engineDisplacementL?f.engineDisplacementL+' L':'');add('cylinders',f.engineCylinders);add('doors',f.doors);
 return next;
}
/** A changed VIN must not silently carry auto-filled facts from its predecessor. */
export function changeVinInput<T extends VehicleInput>(current:T,key:keyof VehicleInput,value:unknown):T {
 const next={...current,fields:{...current.fields}};
 if((key==='identifier'&&String(value??'').trim().toUpperCase()!==current.identifier?.trim().toUpperCase())||(key==='identifierKind'&&current.identifierKind==='VIN'&&value!=='VIN')){
  for(const [id,field] of Object.entries(next.fields)){
   if(!field.source.startsWith(prefix))continue;
   const property=properties[id];
   if(property&&String(current[property]??'')===field.value)(next as Record<string,unknown>)[property]=property==='year'?null:'';
   delete next.fields[id];
  }
 }
 for(const [id,property] of Object.entries(properties))if(property===key)delete next.fields[id];
 (next as Record<string,unknown>)[key]=value;
 return next;
}
/** Editing an imported fact makes it a staff entry, including its core mirror. */
export function editVinFact<T extends VehicleInput>(current:T,id:string,patch:Partial<FieldValue>):T {
 const previous=current.fields?.[id];
 if(!previous)return current;
 const field={...previous,...patch};
 const changed=patch.value!==undefined&&patch.value!==previous.value;
 if(changed&&previous.source.startsWith(prefix)&&patch.source===undefined){
  field.source='Staff entry (edited after VIN lookup)';field.status='reported';delete field.assetId;
 }
 const property=properties[id];
 let value:string|number|null=field.value;
 if(property==='year')value=/^(18|19|20)\d{2}$/.test(field.value.trim())&&Number(field.value)>=1886&&Number(field.value)<=2100?Number(field.value):null;
 if(property==='odometerKm'){
  const match=field.value.trim().match(/^(?:(?:km|kilometres?|kilometers?)\s*[:\-]?\s*)?([\d,]+)\s*(?:km|kilometres?|kilometers?)?$/i);
  const km=match?Number(match[1].replace(/,/g,'')):NaN;
  value=Number.isInteger(km)&&km>=0&&km<=10_000_000?km:null;
 }
 const mirrored=changed&&property?changeVinInput(current,property,value):current;
 return {...mirrored,fields:{...mirrored.fields,[id]:field}};
}
